import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import SellValuationCard from "@/components/valuation/SellValuationCard";
import SellPackagesGrid, { type SellPkg } from "@/components/valuation/SellPackagesGrid";
import { SELL_PACKAGES_COPY } from "@/components/valuation/sellPackagesCopy";
import type { ValuationFormProps } from "@/components/valuation/ValuationForm";
import { saveSellPrefill, snapshotFromSearch, type SellValuationSnapshot } from "@/lib/sellPrefill";
import { trackSellPackageSelect, trackSellValuationPublishClick } from "@/utils/buyerJourneyTracking";
import { useNavigate } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Users, ShieldCheck, Sparkles } from "lucide-react";
import InAppBrowserWarning from "@/components/layout/InAppBrowserWarning";
import { useLang } from "@/i18n/useLang";
import { SELL_BRAND_MAKES } from "@/lib/sellBrandPages";
import { brandSlug } from "@/lib/landingPages";

const PrivateSellLanding = () => {
  const navigate = useNavigate();
  const lang = useLang();
  const t = SELL_PACKAGES_COPY[lang];

  const prefix = lang === "en" ? "/uk" : lang === "de" ? "/de" : "";

  const lastValuation = useRef<SellValuationSnapshot | null>(null);
  const [highlight, setHighlight] = useState(false);
  const [valInitial, setValInitial] = useState<ValuationFormProps['initial']>(undefined);
  const [valKey, setValKey] = useState('empty');
  useEffect(() => {
    if (lang !== 'ro') return;
    const fromUrl = snapshotFromSearch(window.location.search);
    if (fromUrl) {
      setValInitial(fromUrl);
      setValKey('url');
      // Keep the inputs for the final form even if the seller skips the calculation.
      saveSellPrefill({ ...fromUrl, has_estimate: false });
    }
  }, [lang]);

  const choose = (pkg: SellPkg) => {
    if (lang === 'ro') {
      const v = lastValuation.current;
      trackSellPackageSelect(pkg, !!v);
      if (v) saveSellPrefill(v);
    }
    try {
      sessionStorage.setItem("preferredPrivatePkg", pkg);
    } catch {}
    navigate(`${prefix}/vinde-masina/metoda?pkg=${pkg}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t.seoTitle}</title>
        {/* RO description comes only from routeHead("/vinde-masina") to avoid a duplicate tag */}
        {lang !== "ro" && <meta name="description" content={t.seoDesc} />}
        <link rel="canonical" href="https://roautomarket.ro/vinde-masina" />
      </Helmet>

      <main className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <InAppBrowserWarning className="mb-6" />

          <div className="surface-dark band-bleed -mt-12 md:-mt-20 py-6 md:py-10 text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              {t.heading}
            </h1>

            <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-sm md:text-base text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <span>{t.b1}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span>{t.b2}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <span>{t.b3}</span>
              </div>
            </div>
          </div>

          {lang === 'ro' && (
            <SellValuationCard
              initial={valInitial}
              initialKey={valKey}
              onResult={(snap) => { lastValuation.current = snap; }}
              onPublish={(snap) => {
                lastValuation.current = snap;
                trackSellValuationPublishClick(snap.has_estimate);
                setHighlight(true);
                document.getElementById('sell-packages')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          )}

          <Card className="mb-10 border-primary/40 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">{t.aiCardTitle}</h2>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{t.aiCardBody}</p>
            </CardContent>
          </Card>

          <SellPackagesGrid t={t} highlight={highlight} onChoose={choose} />

          {lang === 'ro' && (
            <nav aria-label="Vinde pe marcă" className="mt-10 text-center">
              <h2 className="sans-heading text-base text-foreground mb-3">Vinde pe marcă</h2>
              <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
                {SELL_BRAND_MAKES.map((make) => (
                  <li key={make}>
                    <a href={`/vinde-masina/${brandSlug(make)}`} className="text-primary underline underline-offset-4">
                      {make}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </main>
      <footer className="border-t border-border px-4 py-6 text-center text-sm text-muted-foreground">
        <p>Operator: Vladimir Tudor, comerciant individual înregistrat în Regatul Unit.</p>
        <nav className="mt-2 flex flex-wrap justify-center gap-4" aria-label="Informații legale">
          <a href="/termeni-si-conditii" className="underline">Termeni și condiții</a>
          <a href="/politica-de-confidentialitate" className="underline">Politica de confidențialitate</a>
          <a href="/mentiuni-legale" className="underline">Mențiuni legale</a>
          <a href="/contact" className="underline">Contact</a>
        </nav>
      </footer>
    </div>
  );
};

export default PrivateSellLanding;
